import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Button,
    Alert,
    Checkbox,
    Stack,
} from '@mantine/core';
import {
    IconCheck,
    IconAlertCircle,
    IconArrowLeft
} from '@tabler/icons-react';
import { localTenant } from '@/services/session-service';
import { useGenerateAfipInvoice } from '@/hooks/api-hooks';
import BusinessSelector from '@/components/business-selector';
import CardModal from '@/components/card-modal';
import { notify } from '@/lib/notify';
import { setWebTitle } from '@/lib/format-utils';
import AfipInvoiceFields from '@/components/afip-invoice-fields';
import { AFIP_HISTORY_PATH } from '@/lib/navigation';

export default function GenerateInvoicePage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();
    const generateInvoiceMutation = useGenerateAfipInvoice();

    setWebTitle(t('generate_invoice'));

    // Payer / Receiver State
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [selectedClient, setSelectedClient] = useState<any>(null);
    const [receiverName, setReceiverName] = useState('');
    const [docTipo, setDocTipo] = useState('99'); // Default to Consumidor Final
    const [docNro, setDocNro] = useState('');

    // Invoice Details
    const [invoiceType, setInvoiceType] = useState('1'); // Default to Factura A
    const [pos, setPos] = useState<number | null>(null);
    const [concepto, setConcepto] = useState('2'); // Default to Services
    const [description, setDescription] = useState('');

    // Amounts
    const [totalAmount, setTotalAmount] = useState<number>();
    const [ivaRate, setIvaRate] = useState('5'); // Default to 21%
    const [ivaAmount, setIvaAmount] = useState<number>(0);
    const [netAmount, setNetAmount] = useState<number>(0);

    // Period dates
    const [fchServDesde, setFchServDesde] = useState<Date | null>(new Date());
    const [fchServHasta, setFchServHasta] = useState<Date | null>(new Date());
    const [fchVtoPago, setFchVtoPago] = useState<Date | null>(new Date());
    const [taxCondition, setTaxCondition] = useState<string>('5');

    // Results / Error Modal
    const [successResult, setSuccessResult] = useState<any>(null);
    const [errorData, setErrorData] = useState<any>(null);
    const [errorModalOpened, setErrorModalOpened] = useState(false);

    // Auto calculate IVA and Net
    useEffect(() => {
        const total = typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount) || 0;

        if (invoiceType === '11') { // Factura C
            setIvaAmount(0);
            setNetAmount(total);
        } else {
            let rate = 0.21;
            if (ivaRate === '4') rate = 0.105;
            if (ivaRate === '6') rate = 0.27;
            if (ivaRate === '3') rate = 0; // 0%

            const net = Math.round((total / (1 + rate)) * 100) / 100;
            const iva = Math.round((total - net) * 100) / 100;
            setIvaAmount(iva);
            setNetAmount(net);
        }
    }, [totalAmount, ivaRate, invoiceType]);

    // Set docTipo and docNro to 0 if Consumidor Final
    useEffect(() => {
        if (docTipo === '99') {
            setDocNro('0');
            setTaxCondition('5');
        } else if (docNro === '0') {
            setDocNro('');
        }
    }, [docTipo]);

    const handleSelectClient = (client: any) => {
        setSelectedClient(client);
        setReceiverName(client.name || '');
        const detectedTaxCondition = client.tax_condition || '5';
        if (client.type === 'business') {
            setDocTipo('80'); // CUIT
            setDocNro(client.tax_id_number || '');
            setTaxCondition(detectedTaxCondition);
            setInvoiceType(detectedTaxCondition === '1' ? '1' : '6');
        } else if (client.type === 'person') {
            const afipDocType = client.document_type === 'CUIT' ? '80' : client.document_type === 'CUIL' ? '86' : '96';
            setDocTipo(afipDocType);
            setDocNro(client.document_number || '');
            setTaxCondition(detectedTaxCondition);
            setInvoiceType((afipDocType === '80' && detectedTaxCondition === '1') ? '1' : '6');
        }
    };

    const handleGenerate = () => {
        if (!tenant) return;

        if (!pos || pos <= 0) {
            notify.error(t('pos_required_error'), t('error'));
            return;
        }

        if (!receiverName && docTipo !== '99') {
            notify.error(t('please_select_all_fields', { defaultValue: 'Please fill in all required fields' }), t('error'));
            return;
        }

        if (invoiceType === '1' && docTipo !== '80') {
            notify.error(t('factura_a_requires_cuit'), t('error'));
            return;
        }

        const cleanDocNro = docNro.replace(/\D/g, '');
        if (docTipo !== '99' && (!cleanDocNro || isNaN(parseInt(cleanDocNro, 10)) || parseInt(cleanDocNro, 10) <= 0)) {
            notify.error(t('invalid_doc_number'), t('error'));
            return;
        }

        const parsedTotal = typeof totalAmount === 'number' ? totalAmount : parseFloat(totalAmount);
        if (isNaN(parsedTotal) || parsedTotal <= 0) {
            notify.error(t('please_select_all_fields', { defaultValue: 'Please fill in all required fields' }), t('error'));
            return;
        }

        const data = {
            PtoVta: pos,
            CbteTipo: parseInt(invoiceType, 10),
            Concepto: parseInt(concepto, 10),
            description: description.trim() || undefined,
            DocTipo: parseInt(docTipo, 10),
            DocNro: docTipo === '99' ? 0 : parseInt(cleanDocNro, 10) || 0,
            CondicionIVAReceptorId: docTipo === '99' ? 5 : parseInt(taxCondition, 10) || 5,
            receiver_name: receiverName || 'Consumidor Final',
            ImpTotal: parsedTotal,
            ImpNeto: netAmount,
            ImpIVA: invoiceType === '11' ? 0 : ivaAmount,
            ivaId: invoiceType === '11' ? null : parseInt(ivaRate, 10),
            FchServDesde: (concepto === '2' || concepto === '3') && fchServDesde ? fchServDesde.toISOString().split('T')[0] : undefined,
            FchServHasta: (concepto === '2' || concepto === '3') && fchServHasta ? fchServHasta.toISOString().split('T')[0] : undefined,
            FchVtoPago: (concepto === '2' || concepto === '3') && fchVtoPago ? fchVtoPago.toISOString().split('T')[0] : undefined,
            client_id: selectedClient?.id,
            client_type: selectedClient?.type
        };

        generateInvoiceMutation.mutate({
            tenantId: tenant.id,
            data
        }, {
            onSuccess: (res: any) => {
                setSuccessResult(res);
                notify.success(t('invoice_generated_success'), t('success'));

                // Reset form values
                setSelectedClient(null);
                setReceiverName('');
                setDocNro('');
                setDocTipo('99');
                setTaxCondition('5');
                setDescription('');
                setTotalAmount(0);
            },
            onError: (err: any) => {
                const msg = err.response?.data?.message || err.message || t('error');
                notify.error(msg, t('error'));
                if (err.response?.data) {
                    setErrorData(err.response.data);
                    setErrorModalOpened(true);
                }
            }
        });
    };

    const formatCaeDate = (dateStr?: string) => {
        if (!dateStr || dateStr.length !== 8) return dateStr || '';
        return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
    };

    const isPending = generateInvoiceMutation.isPending;

    return (
        <div className="panel-content">
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">{t('generate_invoice')}</h1>
                <Button
                    size="xs"
                    variant="light"
                    color="gray"
                    leftSection={<IconArrowLeft size={16} />}
                    component={Link}
                    to={AFIP_HISTORY_PATH}
                >
                    {t('back_to_list')}
                </Button>
            </div>

            {successResult && (
                <Alert
                    icon={<IconCheck size={18} />}
                    title={t('invoice_generated_success')}
                    color="green"
                    className="mb-6"
                    withCloseButton
                    onClose={() => setSuccessResult(null)}
                >
                    <div className="flex flex-col gap-2 text-sm mt-1">
                        <div><strong>{t('invoice_number_result')}:</strong> {pos ? pos.toString().padStart(4, '0') : ''}-{successResult.voucherNumber.toString().padStart(8, '0')}</div>
                        <div><strong>{t('cae_result')}:</strong> {successResult.CAE}</div>
                        <div><strong>{t('cae_expiration_result')}:</strong> {formatCaeDate(successResult.CAEFchVto)}</div>
                    </div>
                </Alert>
            )}

            <div className="flex flex-col gap-6">
                {/* Receiver / Customer Section */}
                <Stack gap="md" className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-100 pb-2">
                        {t('receiver_section')}
                    </h3>

                    <Checkbox
                        label={t('manual_entry_toggle')}
                        checked={isManualEntry}
                        onChange={(e) => setIsManualEntry(e.currentTarget.checked)}
                        disabled={isPending}
                    />

                    {!isManualEntry && (
                        <Stack gap="xs">
                            <label className="text-xs font-medium text-gray-700">{t('select_client_selector')}</label>
                            <BusinessSelector
                                selected={selectedClient}
                                onSelect={handleSelectClient}
                                disabled={isPending}
                            />
                        </Stack>
                    )}
                </Stack>

                {/* Details and Amounts Section */}
                <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-100 pb-2 mb-4">
                        {t('invoice_details_section')}
                    </h3>
                    <AfipInvoiceFields
                        receiverName={receiverName}
                        setReceiverName={setReceiverName}
                        docTipo={docTipo}
                        setDocTipo={setDocTipo}
                        docNro={docNro}
                        setDocNro={setDocNro}
                        invoiceType={invoiceType}
                        setInvoiceType={setInvoiceType}
                        pos={pos}
                        setPos={setPos}
                        concepto={concepto}
                        setConcepto={setConcepto}
                        description={description}
                        setDescription={setDescription}
                        ivaRate={ivaRate}
                        setIvaRate={setIvaRate}
                        netAmount={netAmount}
                        ivaAmount={ivaAmount}
                        totalAmount={totalAmount || 0}
                        setTotalAmount={setTotalAmount}
                        fchServDesde={fchServDesde}
                        setFchServDesde={setFchServDesde}
                        fchServHasta={fchServHasta}
                        setFchServHasta={setFchServHasta}
                        fchVtoPago={fchVtoPago}
                        setFchVtoPago={setFchVtoPago}
                        taxCondition={taxCondition}
                        setTaxCondition={setTaxCondition}
                        disabled={isPending}
                        isNameDocEditable={isManualEntry}
                        isTotalAmountEditable={true}
                    />
                </div>

                <div className="flex justify-end gap-4 mt-2">
                    <Button
                        size="md"
                        color="black"
                        onClick={handleGenerate}
                        loading={isPending}
                    >
                        {t('generate_button')}
                    </Button>
                </div>
            </div>

            {/* Error Modal */}
            <CardModal
                opened={errorModalOpened}
                onClose={() => setErrorModalOpened(false)}
                header={
                    <div className="text-lg font-semibold text-red-600 flex items-center gap-2">
                        <IconAlertCircle size={20} />
                        {t('afip_error_detail')}
                    </div>
                }
                size="md"
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                        {t('afip_error_desc')}
                    </p>
                    <pre className="bg-gray-950 text-gray-100 p-4 rounded-lg overflow-auto  text-xs max-h-[300px] border border-gray-800 shadow-inner leading-relaxed select-all">
                        {JSON.stringify(errorData, null, 2)}
                    </pre>
                    <div className="flex justify-end mt-2">
                        <Button variant="light" color="gray" onClick={() => setErrorModalOpened(false)}>
                            {t('close')}
                        </Button>
                    </div>
                </div>
            </CardModal>
        </div>
    );
}
